import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import SignalBadge from "@/components/SignalBadge";
import { calcBreakEven, formatRoas, calcSignalClient } from "@/lib/signal";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function OnboardingFirstLog() {
  const [brand, setBrand] = useState(null);
  const [spend, setSpend] = useState("");
  const [revenue, setRevenue] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Returning-user guard: if they already have logs, go to dashboard.
    if (user?.log_count > 0) {
      navigate("/dashboard", { replace: true });
      return;
    }
    api.get("/brand").then((r) => setBrand(r.data)).catch(() => navigate("/onboarding/brand", { replace: true }));
  }, [user, navigate]);

  const be = brand ? calcBreakEven(brand.selling_price, brand.cogs, brand.other_costs || 0) : 0;
  const roas = useMemo(() => (Number(spend) > 0 ? Number(revenue) / Number(spend) : 0), [spend, revenue]);
  const buffer = roas - be;
  const sig = calcSignalClient(buffer);

  const submit = async (e) => {
    e.preventDefault();
    if (!spend || !revenue) {
      toast.error("Enter ad spend and revenue.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/logs", {
        ad_spend: Number(spend),
        revenue: Number(revenue),
      });
      toast.success("Signal locked in. Welcome to SafeScale.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save log");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-12">
        <Stepper step={2} />
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mt-6">
          Log your first week
        </h1>
        <p className="mt-2 text-slate-600">Two numbers. We'll do the math.</p>

        <Card className="mt-8 p-6 sm:p-8 bg-white rounded-2xl border-slate-200">
          <form onSubmit={submit} className="space-y-5">
            <Money id="fl-spend" label="Ad spend this week" value={spend} onChange={setSpend} testid="firstlog-spend-input" />
            <Money id="fl-rev" label="Revenue this week" value={revenue} onChange={setRevenue} testid="firstlog-revenue-input" />

            <div className="grid grid-cols-2 gap-4">
              <Stat label="ROAS" value={formatRoas(roas)} />
              <Stat label="Break-even" value={formatRoas(be)} />
              <Stat label="Buffer" value={(buffer >= 0 ? "+" : "") + formatRoas(buffer)} accent={buffer >= 0 ? "text-emerald-600" : "text-red-600"} />
              <div className="rounded-xl border border-slate-200 p-4 grid place-items-center">
                <SignalBadge signal={sig} size="lg" />
              </div>
            </div>

            <Button type="submit" disabled={saving} className="w-full h-12 bg-slate-900 hover:bg-slate-800" data-testid="firstlog-submit-button">
              {saving ? "Calculating…" : (<>Get my signal <ArrowRight className="ml-2 h-4 w-4" /></>)}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}

const Money = ({ id, label, value, onChange, testid }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</Label>
    <div className="relative mt-2">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
      <Input id={id} type="number" step="0.01" min="0" value={value} onChange={(e) => onChange(e.target.value)} className="pl-7 h-11" data-testid={testid} />
    </div>
  </div>
);

const Stat = ({ label, value, accent = "text-slate-900" }) => (
  <div className="rounded-xl border border-slate-200 p-4">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <p className={`font-display font-bold text-2xl mt-1 ${accent}`}>{value}</p>
  </div>
);

const Stepper = ({ step }) => (
  <div className="flex items-center gap-3">
    {[1, 2].map((n) => (
      <div key={n} className="flex items-center gap-3">
        <span className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${n <= step ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>
          {n}
        </span>
        {n === 1 && <div className="h-px w-10 bg-slate-200" />}
      </div>
    ))}
    <span className="ml-2 text-xs uppercase tracking-widest text-slate-400 font-bold">Step {step} of 2</span>
  </div>
);
